import React from "react";
import {
  MdArrowOutward,
  MdCalendarMonth,
  MdConfirmationNumber,
  MdDomain,
  MdGroups2,
  MdLocationOn,
  MdOutlineHandshake,
  MdOutlineInsights,
  MdOutlineMail,
  MdOutlinePhone,
  MdOutlinePublic,
  MdSchedule,
  MdVerified,
} from "react-icons/md";

import EventNavigation from "../EventNavigation/EventNavigation";
import Footer from "../Footer/Footer";
import pashaLogo from "./sponsors/pasha.png";
import whiteCityLogo from "./sponsors/white-city.png";
import pmdLogo from "./sponsors/pmd.png";
import seaBreezeLogo from "./sponsors/sea-breeze.png";
import bcrLogo from "./sponsors/bcr.png";
import timesLogo from "./sponsors/times.png";
import "./airex_page.css";

const facts = [
  { value: "03", label: "days in Baku" },
  { value: "20+", label: "countries expected" },
  { value: "10–6", label: "daily opening hours" },
  { value: "FREE", label: "visitor admission" },
];

const pillars = [
  {
    icon: MdOutlineHandshake,
    title: "Meet",
    text: "Come face to face with developers, project owners, brokers, and consultants presenting major real estate, development, and infrastructure projects.",
  },
  {
    icon: MdOutlinePublic,
    title: "Network",
    text: "Join panel discussions with local and international experts, structured B2B meetings, and professional networking across three focused days.",
  },
  {
    icon: MdOutlineInsights,
    title: "Invest",
    text: "Engage the market directly as a buyer or investor, and turn cross-border conversations into new investment and business relationships.",
  },
];

const audiences = [
  {
    title: "Developers & project owners",
    text: "Present residential, commercial, and infrastructure pipelines to an international audience of buyers, investors, and decision-makers.",
  },
  {
    title: "Investors & financiers",
    text: "Source opportunities across Azerbaijan and international markets, and meet the developers, realtors, and institutions behind them.",
  },
  {
    title: "PropTech & built-environment experts",
    text: "Connect architects, designers, technology and smart-solution companies, government representatives, brokers, and consultants in one room.",
  },
];

const outcomes = [
  {
    code: "GATE",
    title: "A strategic gateway",
    text: "Azerbaijan connects Europe, Asia, and the Middle East—placing AIREX at the crossroads of three major markets.",
  },
  {
    code: "DEAL",
    title: "Cross-border deal-making",
    text: "Structured B2B meetings and direct access to decision-makers make partnerships and transactions practical, not theoretical.",
  },
  {
    code: "REACH",
    title: "International exposure",
    text: "Connect international projects with investors, and local opportunities with capital from more than 20 countries.",
  },
];

const sponsors = [
  { name: "PASHA Real Estate", logo: pashaLogo, url: "https://pasharealestate.az/" },
  { name: "Baku White City", logo: whiteCityLogo, url: "https://bakuwhitecity.com/en" },
  { name: "PMD Group", logo: pmdLogo, url: "https://pmdgroup.az/" },
  { name: "Sea Breeze", logo: seaBreezeLogo, url: "https://seabreeze.az/" },
  { name: "Baku City Residences", logo: bcrLogo, url: null },
];

const strategicPartner = {
  name: "TIMES Consulting",
  logo: timesLogo,
  url: "https://timesconsulting.az/",
};

const AirexPage = () => (
  <>
    <EventNavigation />

    <main className="airex-page">
      <section className="airex-hero">
        <div className="airex-hero-grid" />
        <div className="airex-glow airex-glow-one" />
        <div className="airex-glow airex-glow-two" />
        <div className="airex-shell airex-hero-content">
          <div className="airex-hero-copy">
            <p className="airex-kicker"><span /> Azerbaijan International Real Estate Exhibition</p>
            <h1><strong>AIREX</strong><em>Meet. Network. Invest.</em></h1>
            <p className="airex-hero-lede">An international real estate exhibition connecting developers, investors, architects, and PropTech leaders from Azerbaijan and global markets.</p>
            <div className="airex-event-details" aria-label="Event details">
              <span><MdCalendarMonth aria-hidden="true" /> 4–6 November 2026</span>
              <span><MdLocationOn aria-hidden="true" /> Baku Expo Center, Baku, Azerbaijan</span>
            </div>
            <div className="airex-hero-actions">
              <a className="airex-button airex-button-primary" href="https://airex.az/" target="_blank" rel="noopener noreferrer">
                Register for AIREX <MdArrowOutward aria-hidden="true" />
              </a>
              <a className="airex-button airex-button-quiet" href="#why-airex">Why AIREX</a>
            </div>
          </div>
          <aside className="airex-hero-card" aria-label="AIREX event summary">
            <div className="airex-hero-card-mark">01</div>
            <p>First edition</p>
            <h2>Where Europe, Asia, and the Middle East meet.</h2>
            <div className="airex-hero-card-rule" />
            <span><MdVerified aria-hidden="true" /> Free visitor admission, with registration open ahead of the exhibition.</span>
          </aside>
        </div>
      </section>

      <section className="airex-facts" aria-label="AIREX at a glance">
        <div className="airex-shell airex-facts-grid">
          {facts.map((fact) => (
            <div className="airex-fact" key={fact.label}>
              <strong>{fact.value}</strong>
              <span>{fact.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="airex-section airex-introduction">
        <div className="airex-shell airex-introduction-grid">
          <div className="airex-section-heading">
            <p className="airex-kicker airex-kicker-dark">About AIREX 2026</p>
            <h2>A global platform for real estate investment.</h2>
          </div>
          <div className="airex-prose">
            <p>AIREX brings together local and international developers, investors, realtors, architects, technology and smart-solution companies, PropTech businesses, project owners, and urban-development leaders.</p>
            <p>Participants can explore real estate projects, connect with decision-makers, and develop the cross-border partnerships and investment relationships that move markets forward.</p>
          </div>
        </div>
      </section>

      <section className="airex-section airex-pillars-section">
        <div className="airex-shell">
          <div className="airex-section-intro">
            <p className="airex-kicker airex-kicker-dark">What to expect</p>
            <h2>Three days built around meeting, networking, and investing.</h2>
          </div>
          <div className="airex-pillars-grid">
            {pillars.map(({ icon: Icon, title, text }, index) => (
              <article className="airex-pillar" key={title}>
                <span className="airex-pillar-number">0{index + 1}</span>
                <div className="airex-pillar-icon"><Icon aria-hidden="true" /></div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="airex-section airex-why" id="why-airex">
        <div className="airex-shell">
          <div className="airex-why-banner">
            <div>
              <p className="airex-kicker">Why AIREX matters</p>
              <h2>Connect international projects with real decision-makers.</h2>
            </div>
            <p>AIREX is designed to facilitate cross-border partnerships and deal-making, and to open access to opportunities across Azerbaijan and international real estate markets.</p>
          </div>
          <div className="airex-audience-grid">
            {audiences.map((audience) => (
              <article className="airex-audience-card" key={audience.title}>
                <MdGroups2 aria-hidden="true" />
                <h3>{audience.title}</h3>
                <p>{audience.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="airex-section airex-difference">
        <div className="airex-shell airex-difference-layout">
          <div className="airex-difference-copy">
            <p className="airex-kicker airex-kicker-dark">A strategic position</p>
            <h2>More than an exhibition floor.</h2>
            <p>Azerbaijan sits where Europe, Asia, and the Middle East converge. AIREX turns that position into a working platform for investment, partnership, and long-term market access.</p>
            <a className="airex-button airex-difference-button" href="https://airex.az/" target="_blank" rel="noopener noreferrer">
              Explore AIREX 2026 <MdArrowOutward aria-hidden="true" />
            </a>
          </div>
          <div className="airex-outcomes-stack">
            {outcomes.map((outcome) => (
              <article className="airex-outcome" key={outcome.code}>
                <span>{outcome.code}</span>
                <div><h3>{outcome.title}</h3><p>{outcome.text}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="airex-section airex-sponsors">
        <div className="airex-shell">
          <div className="airex-section-intro">
            <p className="airex-kicker airex-kicker-dark">Backed by the market</p>
            <h2>Main sponsors and partners.</h2>
          </div>
          <div className="airex-sponsors-grid">
            {sponsors.map(({ name, logo, url }) => {
              const content = <img src={logo} alt={`${name} logo`} loading="lazy" />;
              return url ? (
                <a className="airex-sponsor" key={name} href={url} target="_blank" rel="noopener noreferrer" aria-label={name}>
                  {content}
                </a>
              ) : (
                <div className="airex-sponsor" key={name}>{content}</div>
              );
            })}
          </div>

          <div className="airex-partner-row">
            <p className="airex-partner-label">Strategic partner</p>
            <a
              className="airex-sponsor airex-sponsor-partner"
              href={strategicPartner.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={strategicPartner.name}
            >
              <img src={strategicPartner.logo} alt={`${strategicPartner.name} logo`} loading="lazy" />
            </a>
          </div>
        </div>
      </section>

      <section className="airex-section airex-location">
        <div className="airex-shell airex-location-card">
          <div className="airex-location-icon"><MdDomain aria-hidden="true" /></div>
          <div>
            <p className="airex-kicker airex-kicker-dark">Baku Expo Center</p>
            <h2>515 Heydar Aliyev Avenue, Baku.</h2>
          </div>
          <div className="airex-location-detail">
            <p>Organised by Baku Expo Management LLC, AIREX 2026 runs across three full days at Baku Expo Center, AZ1050, Azerbaijan.</p>
            <ul className="airex-location-list">
              <li><MdSchedule aria-hidden="true" /> 10:00 AM – 6:00 PM daily</li>
              <li><MdConfirmationNumber aria-hidden="true" /> Free admission, visitor registration available</li>
              <li><MdOutlineMail aria-hidden="true" /> <a href="mailto:info@airex.az">info@airex.az</a></li>
              <li><MdOutlinePhone aria-hidden="true" /> <a href="tel:+994125998776">+994 12 599 87 76</a> <em>(or *0776)</em></li>
            </ul>
          </div>
        </div>
      </section>

      <section className="airex-closing">
        <div className="airex-shell airex-closing-inner">
          <p className="airex-kicker">4–6 November 2026</p>
          <h2>Be part of Azerbaijan's real estate gateway.</h2>
          <p>Join AIREX 2026 in Baku and connect with the developers, investors, and innovators shaping the region's property market.</p>
          <a className="airex-button airex-button-primary" href="https://airex.az/" target="_blank" rel="noopener noreferrer">Visit airex.az <MdArrowOutward aria-hidden="true" /></a>
        </div>
      </section>
    </main>

    <Footer />
  </>
);

export default AirexPage;
